import { useEffect, useState } from "react";
import AppToast from "../../../components/AppToast";
import BaseModal from "../../../components/BaseModal";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";

import { Pie, Bar, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler
);

export default function AdminDashboard() {
  const [reportType, setReportType] = useState("loans");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStart, setDownloadStart] = useState("");
  const [downloadEnd, setDownloadEnd] = useState("");
  const [downloadStatus, setDownloadStatus] = useState("");

  const [toast, setToast] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/admin_dashboard_stats.php`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            startDate,
            endDate,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load dashboard");
      }

      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDownload = () => {
    if (!downloadStart || !downloadEnd) {
      setToast({
        type: "error",
        title: "Missing Dates",
        message: "Please select both From Date and To Date.",
      });
      return;
    }

    if (downloadStart > downloadEnd) {
      setToast({
        type: "error",
        title: "Invalid Date Range",
        message: "From Date cannot be greater than To Date.",
      });
      return;
    }

    const params = new URLSearchParams();
    params.append("startDate", downloadStart);
    params.append("endDate", downloadEnd);

    if (downloadStatus && reportType === "loans") {
      params.append("status", downloadStatus);
    }

    const endpoint =
      reportType === "staff"
        ? "export_staff_summary.php"
        : "export_loans.php";

    window.open(
      `${process.env.REACT_APP_API_BASE}/${endpoint}?${params.toString()}`,
      "_blank"
    );

    setShowDownloadModal(false);

    setToast({
      type: "success",
      title: "Download Started",
      message: "Your report is being generated.",
    });
  };

  if (loading && !stats) return <DashboardLoader />;

  if (error) {
    return (
      <div className="admin-premium-error">
        <h4>Unable to load dashboard</h4>
        <p>{error}</p>
        <button onClick={fetchStats}>Try Again</button>
      </div>
    );
  }

  const npl = Number(stats?.kpi?.npl || 0);

  return (
    <div className="admin-premium-dashboard">
      {loading && stats && (
        <div className="admin-refresh-overlay">
          <div className="admin-mini-loader" />
        </div>
      )}

      <section className="admin-hero">
        <div>
          <span className="admin-hero-badge">
            <i className="fa fa-shield-alt" />
            Admin Intelligence
          </span>

          <h2>Admin Dashboard</h2>

          <p>
            System overview, loan analytics, portfolio performance and
            administrative controls.
          </p>
        </div>

        <div className="admin-dashboard-switch">
          <button
            type="button"
            className={activeTab === "dashboard" ? "active" : ""}
            onClick={() => setActiveTab("dashboard")}
          >
            <i className="fa fa-layer-group" />
            Dashboard
          </button>

          <button
            type="button"
            className={activeTab === "analytics" ? "active" : ""}
            onClick={() => setActiveTab("analytics")}
          >
            <i className="fa fa-chart-line" />
            Analytics
          </button>
        </div>
      </section>

      {activeTab === "dashboard" && (
        <>
          <section className="admin-filter-card">
            <div className="admin-filter-left">
              <div>
                <label>From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <label>To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="admin-primary-btn"
                onClick={fetchStats}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="admin-btn-spinner" />
                    Filtering...
                  </>
                ) : (
                  <>
                    <i className="fa fa-filter" />
                    Apply Filter
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              className="admin-outline-btn"
              onClick={() => setShowDownloadModal(true)}
            >
              <i className="fa fa-download" />
              Download Report
            </button>
          </section>

          <section className="admin-kpi-grid">
         <AdminKPI
  title="Total Customers"
  value={stats?.kpi?.totalCustomers?.toLocaleString() || 0}
  icon="fa-users"
  tone="blue"
  trend="+12% MTD"
  progress={72}
/>

<AdminKPI
  title="Active Loans"
  value={stats?.kpi?.activeLoans?.toLocaleString() || 0}
  icon="fa-file-invoice-dollar"
  tone="green"
  trend="Live"
  progress={82}
/>

<AdminKPI
  title="Loan Portfolio"
  value={`₦${Number(stats?.kpi?.loanPortfolio || 0).toLocaleString()}`}
  icon="fa-coins"
  tone="cyan"
  trend="This Month"
  progress={68}
/>

<AdminKPI
  title="Non-Performing Loans"
  value={`${npl}%`}
  icon="fa-exclamation-triangle"
  tone="amber"
  trend={npl > 10 ? "High Risk" : npl > 5 ? "Watch" : "Low Risk"}
  progress={npl > 10 ? 85 : npl > 5 ? 55 : 28}
/>
          </section>

          <section className="admin-insight-grid">
            <InsightCard
              icon="fa-chart-line"
              title="Portfolio Health"
              value={
                npl > 10 ? "High Risk" : npl > 5 ? "Moderate Risk" : "Healthy"
              }
              text="Risk condition is calculated from the current non-performing loan exposure."
            />

            <InsightCard
              icon="fa-bolt"
              title="System Status"
              value="Operational"
              text="Core dashboard services and reporting endpoints are currently available."
            />

            <InsightCard
              icon="fa-user-shield"
              title="Admin Controls"
              value="Active"
              text="Administrative tools and reporting access are enabled for this workspace."
            />
          </section>

          <section className="admin-content-grid">
            <div className="admin-premium-card">
              <div className="admin-card-header">
                <div>
                  <h6>Loan Portfolio Analysis</h6>
                  <p>Summary of loan value movement and application quality.</p>
                </div>
                <span>Overview</span>
              </div>

              <div className="analysis-grid">
                <AnalysisItem
                  label="Approved Loans"
                  value={`₦${Number(
                    stats?.analysis?.approved || 0
                  ).toLocaleString()}`}
                />

                <AnalysisItem
                  label="Pending Loans"
                  value={`₦${Number(
                    stats?.analysis?.pending || 0
                  ).toLocaleString()}`}
                />

                <AnalysisItem
                  label="Rejected Loans"
                  value={`₦${Number(
                    stats?.analysis?.rejected || 0
                  ).toLocaleString()}`}
                />

                <AnalysisItem
                  label="Average Loan Size"
                  value={`₦${Number(
                    stats?.analysis?.averageLoanSize || 0
                  ).toLocaleString()}`}
                />
              </div>

              <ul className="admin-note-list">
                <li>Total loan portfolio reflects approved disbursements.</li>
                <li>Pending loans represent applications under review.</li>
                <li>NPL is calculated based on overdue exposure.</li>
              </ul>
            </div>

            <div className="admin-premium-card">
              <div className="admin-card-header">
                <div>
                  <h6>Risk & System Health</h6>
                  <p>Operational and credit risk indicators.</p>
                </div>
                <span>Live</span>
              </div>

              <HealthItem label="System Status" value="Operational" tone="success" />
              <HealthItem
                label="Credit Risk Level"
                value={npl > 10 ? "High" : npl > 5 ? "Moderate" : "Low"}
                tone={npl > 10 ? "danger" : npl > 5 ? "warning" : "success"}
              />
              <HealthItem label="Liquidity Ratio" value="Healthy" tone="success" />
              <HealthItem label="Regulatory Flags" value="None" tone="success" />
            </div>
          </section>
        </>
      )}

      {activeTab === "analytics" && <AnalyticsTab stats={stats} />}

      <BaseModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={
          reportType === "staff"
            ? "Download Staff Summary Report"
            : "Download Loan Detailed Report"
        }
        size="md"
        icon={<i className="fa fa-download text-primary" />}
        footer={
          <>
            <button
              className="btn btn-light"
              onClick={() => setShowDownloadModal(false)}
            >
              Cancel
            </button>

            <button className="btn btn-primary" onClick={handleDownload}>
              <i className="fa fa-download me-2" />
              Download
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Report Type</label>
          <select
            className="form-select"
            value={reportType}
            onChange={(e) => {
              const value = e.target.value;
              setReportType(value);

              if (value === "staff") {
                setDownloadStatus("");
              }
            }}
          >
            <option value="loans">Loan Detailed Report</option>
            <option value="staff">Staff Summary Report</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">From Date</label>
          <input
            type="date"
            className="form-control"
            value={downloadStart}
            onChange={(e) => setDownloadStart(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">To Date</label>
          <input
            type="date"
            className="form-control"
            value={downloadEnd}
            onChange={(e) => setDownloadEnd(e.target.value)}
          />
        </div>

        {reportType === "loans" && (
          <div className="mb-3">
            <label className="form-label">Loan Status</label>
            <select
              className="form-select"
              value={downloadStatus}
              onChange={(e) => setDownloadStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="reviewing">Reviewing</option>
              <option value="level_one_approved">Level One Approved</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="returned">Returned</option>
              <option value="disbursed">Disbursed</option>
            </select>
          </div>
        )}
      </BaseModal>

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function AnalyticsTab({ stats }) {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
        },
      },
    },
  };

  return (
    <section className="analytics-wrapper premium-analytics">
      <ChartCard title="Loan Status Distribution" badge="Status Mix">
        <Pie
          data={{
            labels: stats?.statusBreakdown?.map((s) => s.status) || [],
            datasets: [
              {
                data: stats?.statusBreakdown?.map((s) => s.total) || [],
                backgroundColor: [
                  "#2563eb",
                  "#16a34a",
                  "#facc15",
                  "#dc2626",
                  "#6b7280",
                ],
                borderWidth: 0,
              },
            ],
          }}
          options={chartOptions}
        />
      </ChartCard>

      <ChartCard title="Monthly Disbursement" badge="MTD">
        <Bar
          data={{
            labels: stats?.monthlyTrend?.map((m) => m.month) || [],
            datasets: [
              {
                label: "Disbursement",
                data: stats?.monthlyTrend?.map((m) => m.total) || [],
                backgroundColor: "#2563eb",
                borderRadius: 12,
              },
            ],
          }}
          options={{
            ...chartOptions,
            plugins: { legend: { display: false } },
            scales: {
              y: { beginAtZero: true, grid: { color: "#eef2f7" } },
              x: { grid: { display: false } },
            },
          }}
        />
      </ChartCard>

      <ChartCard title="Loan Product Distribution" badge="Products">
        <Pie
          data={{
            labels: stats?.productBreakdown?.map((p) => p.loan_product) || [],
            datasets: [
              {
                data: stats?.productBreakdown?.map((p) => p.total) || [],
                backgroundColor: [
                  "#0ea5e9",
                  "#22c55e",
                  "#f97316",
                  "#a855f7",
                  "#ef4444",
                ],
                borderWidth: 0,
              },
            ],
          }}
          options={chartOptions}
        />
      </ChartCard>

      <ChartCard title="Approval vs Rejection Ratio" badge="Quality">
        <Pie
          data={{
            labels: ["Approved", "Rejected"],
            datasets: [
              {
                data: [
                  stats?.analysis?.approved || 0,
                  stats?.analysis?.rejected || 0,
                ],
                backgroundColor: ["#16a34a", "#dc2626"],
                borderWidth: 0,
              },
            ],
          }}
          options={chartOptions}
        />
      </ChartCard>

      <ChartCard title="Portfolio Growth Trend" badge="Trend">
        <Line
          data={{
            labels: stats?.monthlyTrend?.map((m) => m.month) || [],
            datasets: [
              {
                label: "Portfolio Value",
                data: stats?.monthlyTrend?.map((m) => m.total) || [],
                borderColor: "#2563eb",
                backgroundColor: "rgba(37, 99, 235, 0.12)",
                tension: 0.45,
                fill: true,
                pointRadius: 4,
                pointBackgroundColor: "#2563eb",
              },
            ],
          }}
          options={{
            ...chartOptions,
            plugins: { legend: { display: false } },
            scales: {
              y: { beginAtZero: true, grid: { color: "#eef2f7" } },
              x: { grid: { display: false } },
            },
          }}
        />
      </ChartCard>

      <div className="chart-box risk-indicator-card">
        <div className="admin-card-header">
          <div>
            <h6>Credit Risk Exposure</h6>
            <p>Non-performing loan rate.</p>
          </div>
          <span>Risk</span>
        </div>

        <div className="risk-indicator">
          <h2>{stats?.kpi?.npl || 0}%</h2>
          <p>Non-Performing Loan Rate</p>
        </div>
      </div>
    </section>
  );
}

function AdminKPI({ title, value, icon, tone, trend, progress = 68 }) {
  return (
    <div className={`admin-kpi-card ${tone}`}>
      <div className="admin-kpi-top">
        <div className="admin-kpi-icon">
          <i className={`fa ${icon}`} />
        </div>
        <span>{trend}</span>
      </div>

      <small>{title}</small>
      <h4>{value}</h4>

      <div className="admin-kpi-progress">
  <i style={{ width: `${progress}%` }} />
</div>
    </div>
  );
}

function InsightCard({ icon, title, value, text }) {
  return (
    <div className="admin-insight-card">
      <div className="admin-insight-icon">
        <i className={`fa ${icon}`} />
      </div>

      <div>
        <span>{title}</span>
        <strong>{value}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function ChartCard({ title, badge, children }) {
  return (
    <div className="chart-box">
      <div className="admin-card-header">
        <div>
          <h6>{title}</h6>
          <p>Real-time dashboard analytics.</p>
        </div>
        <span>{badge}</span>
      </div>

      <div className="chart-container">{children}</div>
    </div>
  );
}

function AnalysisItem({ label, value }) {
  return (
    <div className="analysis-card">
      <div className="analysis-value">{value}</div>
      <div className="analysis-label">{label}</div>
    </div>
  );
}

function HealthItem({ label, value, tone }) {
  return (
    <div className="admin-health-item">
      <span>{label}</span>
      <strong className={tone}>{value}</strong>
    </div>
  );
}

function DashboardLoader() {
  return (
    <div className="admin-dashboard-loader">
      <div className="admin-dashboard-loader-card">
        <div className="admin-loader-orb">
          <span />
        </div>

        <h6>Loading admin dashboard...</h6>
        <p>Preparing secure banking analytics workspace</p>
      </div>
    </div>
  );
}